import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * A number that eases from its previous value to the new one instead of
 * jumping — the landing page's till total and plan price count up this way.
 *
 * Starts from the first value it is given (no count-up on mount), and snaps
 * straight to the target when reduced motion is on.
 *
 * @param {number} target
 * @param {number} [duration] - ms
 * @returns {number} the value to display this frame, rounded
 */
export function useAnimatedNumber(target, duration = 420) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    if (reduced || from === target) {
      setShown(target);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(from + (target - from) * eased));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, reduced]);

  return shown;
}

export default useAnimatedNumber;
